import { z } from 'zod';

/**
 * RFL-131 — terminal flow rule vocabulary + helpers, shared by the
 * graphical canvas and the JSON/declarative editor so both speak the
 * exact same shape. The zod schema mirrors
 * `packages/contracts/schemas/domain/terminal-flow.ts`; keep them in sync
 * (a contract change is a pin-bumping event — see docs/cross-repo).
 */

export type FlowSurface = 'pos' | 'sco' | 'attendant';

export const TRIGGERS = [
  'pos.line.void',
  'pos.discount.applied',
  'pos.return.line',
  'pos.no_sale',
  'sco.line.void',
  'sco.unknown_barcode',
  'sco.age_required',
  'sco.weight_mismatch',
  'sco.manual_price',
] as const;
export type FlowTrigger = (typeof TRIGGERS)[number];

export const ACTIONS = [
  'require_attendant_approval',
  'require_supervisor_pin',
  'log_only',
  'block',
  'prompt_customer',
] as const;
export type FlowAction = (typeof ACTIONS)[number];

export const CONDITION_KINDS = [
  'always',
  'amount_over',
  'cashier_role_not',
  'time_of_day_in',
] as const;
export type FlowConditionKind = (typeof CONDITION_KINDS)[number];

export type FlowCondition =
  | { kind: 'always' }
  | { kind: 'amount_over'; amountMinor: number }
  | { kind: 'cashier_role_not'; role: string }
  | { kind: 'time_of_day_in'; from: string; to: string };

export type FlowRule = {
  id: string;
  trigger: FlowTrigger;
  condition: FlowCondition;
  action: FlowAction;
  enabled: boolean;
  order: number;
  note?: string;
};

// ---------- Labels (human-facing) ----------

export const TRIGGER_LABEL: Record<FlowTrigger, string> = {
  'pos.line.void': 'POS · void line',
  'pos.discount.applied': 'POS · discount applied',
  'pos.return.line': 'POS · return line',
  'pos.no_sale': 'POS · no sale (drawer open)',
  'sco.line.void': 'SCO · void line',
  'sco.unknown_barcode': 'SCO · unknown barcode',
  'sco.age_required': 'SCO · age-restricted item',
  'sco.weight_mismatch': 'SCO · weight mismatch',
  'sco.manual_price': 'SCO · manual price entry',
};

export const ACTION_LABEL: Record<FlowAction, string> = {
  require_attendant_approval: 'Require attendant approval',
  require_supervisor_pin: 'Require supervisor PIN',
  log_only: 'Log only',
  block: 'Block',
  prompt_customer: 'Prompt customer',
};

export const CONDITION_LABEL: Record<FlowConditionKind, string> = {
  always: 'Always',
  amount_over: 'Amount over…',
  cashier_role_not: 'Cashier role is not…',
  time_of_day_in: 'Time of day in…',
};

/**
 * Catalogue keys for the three label maps above.
 *
 * The maps stay as the baked English, so the canvas resolves an option label as
 * `t(TRIGGER_LABEL_KEY[x], TRIGGER_LABEL[x])`. Each key is written out per enum
 * member rather than composed from the value at the call site: the parity guard
 * reads source for literal keys.
 */
export const TRIGGER_LABEL_KEY: Record<FlowTrigger, string> = {
  'pos.line.void': 'terminalFlows.vocab.triggerPosLineVoid',
  'pos.discount.applied': 'terminalFlows.vocab.triggerPosDiscountApplied',
  'pos.return.line': 'terminalFlows.vocab.triggerPosReturnLine',
  'pos.no_sale': 'terminalFlows.vocab.triggerPosNoSale',
  'sco.line.void': 'terminalFlows.vocab.triggerScoLineVoid',
  'sco.unknown_barcode': 'terminalFlows.vocab.triggerScoUnknownBarcode',
  'sco.age_required': 'terminalFlows.vocab.triggerScoAgeRequired',
  'sco.weight_mismatch': 'terminalFlows.vocab.triggerScoWeightMismatch',
  'sco.manual_price': 'terminalFlows.vocab.triggerScoManualPrice',
};

export const ACTION_LABEL_KEY: Record<FlowAction, string> = {
  require_attendant_approval: 'terminalFlows.vocab.actionRequireAttendantApproval',
  require_supervisor_pin: 'terminalFlows.vocab.actionRequireSupervisorPin',
  log_only: 'terminalFlows.vocab.actionLogOnly',
  block: 'terminalFlows.vocab.actionBlock',
  prompt_customer: 'terminalFlows.vocab.actionPromptCustomer',
};

export const CONDITION_LABEL_KEY: Record<FlowConditionKind, string> = {
  always: 'terminalFlows.vocab.conditionAlways',
  amount_over: 'terminalFlows.vocab.conditionAmountOver',
  cashier_role_not: 'terminalFlows.vocab.conditionCashierRoleNot',
  time_of_day_in: 'terminalFlows.vocab.conditionTimeOfDayIn',
};

/** Which surface a trigger belongs to (the segment before the first dot). */
export function triggerSurface(trigger: FlowTrigger): FlowSurface {
  return trigger.startsWith('sco.') ? 'sco' : trigger.startsWith('pos.') ? 'pos' : 'attendant';
}

/** Triggers relevant to a given surface (attendant flows reuse SCO ones). */
export function triggersForSurface(surface: FlowSurface): FlowTrigger[] {
  if (surface === 'pos') return TRIGGERS.filter((t) => t.startsWith('pos.'));
  return TRIGGERS.filter((t) => t.startsWith('sco.'));
}

/** A short, stable client id for a new rule (no Math.random in tests). */
let ruleCounter = 0;
export function newRuleId(): string {
  ruleCounter += 1;
  return `r-${Date.now().toString(36)}-${ruleCounter}`;
}

/** A blank rule for the given surface, appended at `order`. */
export function newRule(surface: FlowSurface, order: number): FlowRule {
  return {
    id: newRuleId(),
    trigger: triggersForSurface(surface)[0] ?? TRIGGERS[0],
    condition: { kind: 'always' },
    action: 'require_attendant_approval',
    enabled: true,
    order,
  };
}

/** Default condition payload when switching a rule's condition kind. */
export function defaultCondition(kind: FlowConditionKind): FlowCondition {
  switch (kind) {
    case 'amount_over':
      return { kind, amountMinor: 0 };
    case 'cashier_role_not':
      return { kind, role: 'supervisor' };
    case 'time_of_day_in':
      return { kind, from: '22:00', to: '06:00' };
    default:
      return { kind: 'always' };
  }
}

export function describeCondition(c: FlowCondition): string {
  switch (c.kind) {
    case 'amount_over':
      return `amount > ${(c.amountMinor / 100).toFixed(2)}`;
    case 'cashier_role_not':
      return `role ≠ ${c.role}`;
    case 'time_of_day_in':
      return `${c.from}–${c.to}`;
    default:
      return 'always';
  }
}

// ---------- zod validation (mirrors the contract) ----------

const conditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('always') }),
  z.object({ kind: z.literal('amount_over'), amountMinor: z.number().int().min(0) }),
  z.object({ kind: z.literal('cashier_role_not'), role: z.string().min(1).max(64) }),
  z.object({
    kind: z.literal('time_of_day_in'),
    from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  }),
]);

export const ruleSchema = z.object({
  id: z.string().min(1).max(64),
  trigger: z.enum(TRIGGERS),
  condition: conditionSchema,
  action: z.enum(ACTIONS),
  enabled: z.boolean(),
  order: z.number().int().min(0),
  note: z.string().max(500).optional(),
});

export const rulesSchema = z.array(ruleSchema).max(500);

export type ParseResult = { ok: true; rules: FlowRule[] } | { ok: false; error: string };

/** Parse + validate a rules JSON document for the declarative editor. */
export function parseRulesJson(text: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    return { ok: false, error: `Invalid JSON: ${(err as Error).message}` };
  }
  const result = rulesSchema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    return {
      ok: false,
      error: first ? `${first.path.join('.') || '(root)'}: ${first.message}` : 'Invalid rules',
    };
  }
  return { ok: true, rules: result.data as FlowRule[] };
}
